import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Chip,
  Avatar,
  Box,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const UsersTable = ({ users, onDeleteUser, onEditUser, onToggleUserStatus }) => {
  const [orderBy, setOrderBy] = useState('fullName');
  const [order, setOrder] = useState('asc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle string comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'success';
      case 'UNCONFIRMED':
        return 'warning';
      case 'FORCE_CHANGE_PASSWORD':
        return 'info';
      case 'ARCHIVED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInitials = (fullName) => {
    if (!fullName || fullName === 'Unknown User') {
      return '??';
    }
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'fullName'}
                direction={orderBy === 'fullName' ? order : 'asc'}
                onClick={() => handleSort('fullName')}
              >
                User
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleSort('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'status'}
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={() => handleSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'createdDate'}
                direction={orderBy === 'createdDate' ? order : 'asc'}
                onClick={() => handleSort('createdDate')}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map((user) => (
            <TableRow key={user.username} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getInitials(user.fullName)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      @{user.username}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={user.status}
                    color={getStatusColor(user.status)}
                    size="small"
                    variant="outlined"
                  />
                  {!user.enabled && (
                    <Chip
                      label="DISABLED"
                      color="error"
                      size="small"
                      variant="filled"
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>{user.createdDate}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {user.isAdmin ? (
                    <>
                      <AdminPanelSettingsIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="primary">
                        Admin
                      </Typography>
                    </>
                  ) : (
                    <>
                      <PersonIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        User
                      </Typography>
                    </>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={() => onEditUser && onEditUser(user)}
                  color="primary"
                  size="small"
                  title="Edit user"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => onToggleUserStatus && onToggleUserStatus(user)}
                  color={user.enabled ? "warning" : "success"}
                  size="small"
                  title={user.enabled ? "Disable user" : "Enable user"}
                >
                  {user.enabled ? <BlockIcon /> : <CheckCircleIcon />}
                </IconButton>
                <IconButton
                  onClick={() => onDeleteUser && onDeleteUser(user)}
                  color="error"
                  size="small"
                  title="Delete user"
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {sortedUsers.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                  No users found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
